// barrel(`../components`) 이 아니라 파일 경로로 직접 가져온다 — barrel 은 utils 를 쓰는
// 다른 컴포넌트까지 함께 끌어와 순환 참조가 열린다.
import { openAiBuilderAuthExpiredDialog } from '../components/AiBuilderAuthExpiredDialog';
import { type AiBuilderChatState } from '../store/types';

import {
  resolveAiBuilderChatRestorePath,
  saveAiBuilderChatRestoreSnapshot,
} from './aiBuilderChatRestore';
import { trackAiBuilderAuthExpired } from './aiBuilderChatTracking';
import { type AiBuilderAuthExpiredPlace } from './events.quiz-ai-builder';
import { isServerAiBuilderChatHttpError } from './serverAiBuilderChatTransport';
import { type AiBuilderChatOrchestrationEvent } from './types';

/** 확인 후 재로그인하고 돌아올 목적지의 기본값 — 빌더 신규 작성 화면. */
const DEFAULT_RETURN_PATH = '/build';

export const isAiBuilderAuthExpiredEvent = (
  event: AiBuilderChatOrchestrationEvent,
): boolean => event.type === 'authExpired';

export const isAiBuilderAuthExpiredError = (error: unknown): boolean => {
  if (!isServerAiBuilderChatHttpError(error)) return false;

  return error.status === 401 || error.code === 'AUTH_EXPIRED';
};

let authExpiredHandled = false;

/** 테스트 전용 — 모듈 스코프 1회 가드를 초기화한다. */
export const resetAiBuilderAuthExpiredGuardForTest = () => {
  authExpiredHandled = false;
};

type HandleAuthExpiredParams = {
  getChatState: () => AiBuilderChatState;
  draftInstanceId: string;
  /** 만료가 도착한 채널. 계측 축이자 원인 분석의 출발점. */
  place: AiBuilderAuthExpiredPlace;
  /** 복원 후 이어서 발급할 메시지 id 카운터 — 시드하지 않으면 id 가 충돌한다. */
  nextRequestId: number;
  nextRunId: number;
};

/**
 * 로그인 만료 처리 단일 진입점.
 *
 * 만료는 두 채널로 도착한다 — SSE 턴은 `authExpired` 이벤트, JSON 경로(세션 생성 등)는
 * `ServerAiBuilderChatHttpError`. 두 경로 모두 여기로 모아 (1) 대화 스냅샷 저장
 * (2) 닫을 수 없는 재로그인 모달 노출을 **한 번만** 수행한다.
 */
export const handleAiBuilderAuthExpired = ({
  getChatState,
  draftInstanceId,
  place,
  nextRequestId,
  nextRunId,
}: HandleAuthExpiredParams) => {
  // 한 턴에서 여러 만료 신호가 겹쳐 도착할 수 있다(SSE error + 후속 요청 401).
  // 모달이 중첩되지 않도록 첫 신호만 처리한다.
  if (authExpiredHandled) return;
  authExpiredHandled = true;

  const path = resolveAiBuilderChatRestorePath() || DEFAULT_RETURN_PATH;
  const state = getChatState();

  const snapshotSkipReason = saveAiBuilderChatRestoreSnapshot({
    state,
    draftInstanceId,
    path,
    now: Date.now(),
    nextRequestId,
    nextRunId,
  });

  // 중복 가드 안쪽에서 발화한다 — 만료 1건당 1회를 보장하는 지점이 여기뿐이다.
  trackAiBuilderAuthExpired({
    place,
    sessionId: state.sessionId ?? '',
    messageCount: state.messages.length,
    hadActiveTurn: state.turnInProgress,
    snapshotSkipReason,
  });

  openAiBuilderAuthExpiredDialog({ onConfirm: () => redirectToLogin(path) });
};

/**
 * hard navigation 을 쓴다 — router.push 로는 만료 토큰 상태의 react-query 캐시와
 * 메모리 상태가 남아 재로그인 후에도 낡은 권한 판단이 이어질 수 있다.
 */
const redirectToLogin = (callbackPath: string) => {
  if (typeof window === 'undefined') return;

  window.location.assign(
    `/login?callbackUrl=${encodeURIComponent(callbackPath)}`,
  );
};
